import type { Product } from "../../products";
import { BUNDLE_CHECKOUT_URL, PRICING } from "@/lib/pricing-constants";
import { getProductScreenshots } from "@/lib/utils/get-product-screenshots";

export const videoDownloaderProProduct: Product = {
  id: "video-downloader-pro",
  slug: "video-downloader-pro",
  name: "Video Downloader Pro — Save X, Facebook, Instagram, TikTok in 4K",
  metaTitle: "Video Downloader Pro — Save X, Instagram, TikTok in 4K",
  alternateName: "Video Downloader Pro",
  shortName: "Video Downloader",
  url: "https://plugmonkey.xyz/product/video-downloader-pro/",
  chromeStoreRedirect: "/redirect/video-downloader-pro-chrome-store/",
  description:
    "Free Chrome extension that auto-detects and downloads videos from X, Facebook, Instagram, TikTok & 1000+ sites in HD, 4K, 8K. No signup.",
  shortDescription:
    "Download videos from any website in HD, 4K, 8K with smart detection and thumbnail previews.",
  image: ["https://plugmonkey.xyz/product/video-downloader-pro/og-image.png"],
  icon: {
    name: "HiDownload",
    backgroundClasses: "bg-red-500",
    color: "#ffffff",
  },
  category: "Software > Browser Extensions > Video Download Tools",
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  audienceType:
    "Content Creators, Students, Marketers, Researchers, Video Editors, Podcasters, Journalists, Business Professionals",
  freeOffer: {
    name: "Free Version",
    description:
      "5 video downloads per week, all quality options (HD, 4K, 8K when available), basic thumbnail previews, standard download speeds",
    price: "0",
    priceCurrency: "USD",
    url: "https://plugmonkey.xyz/redirect/video-downloader-pro-chrome-store/",
  },
  offers: {
    monthly: {
      name: "Monthly Subscription",
      description: "Pay as you go. Cancel anytime.",
      price: PRICING.MONTHLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/01420472-0016-4833-8052-6f2837917789",
    },
    yearly: {
      name: "Yearly Subscription",
      description: `Save ~${PRICING.YEARLY_SAVINGS_PERCENT}% vs monthly billing.`,
      price: PRICING.YEARLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/74adfc5f-096a-4244-a291-fb5f5b52c237",
    },
    lifetime: {
      name: "Lifetime License",
      description: "One-time payment. Own it forever.",
      price: PRICING.LIFETIME,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/c15105d7-7297-44fd-818c-7f409672bae3",
      priceValidUntil: "2030-12-31",
      warranty: {
        durationOfWarranty: "P30D",
        warrantyScope: "30-day money-back guarantee",
      },
    },
  },
  bundleOffer: {
    name: "All-Access Bundle",
    description: "Get every extension we build, forever.",
    price: PRICING.BUNDLE_LIFETIME,
    priceCurrency: "USD",
    url: BUNDLE_CHECKOUT_URL,
  },
  aggregateRating: {
    ratingValue: "4.9",
    reviewCount: "127",
    bestRating: "5",
    worstRating: "1",
  },
  userCount: "32,410",
  keywords:
    "video downloader chrome extension, download videos from any website, 4K video downloader, HD video downloader, save streaming videos, download embedded videos, video grabber, video download manager, chrome video extractor, youtube downloader alternative, video capture chrome",
  additionalProperties: [
    {
      name: "Platform",
      value: "Chrome Extension",
    },
    {
      name: "Version",
      value: "1.8.9",
    },
    {
      name: "License Type",
      value: "Lifetime",
    },
    {
      name: "Video Detection Methods",
      value: "4 proprietary detection methods",
    },
    {
      name: "Supported Resolutions",
      value: "SD to 8K Ultra HD",
    },
    {
      name: "Video Formats",
      value: "MP4, WebM, HLS, DASH, AVI, MOV, MKV, FLV",
    },
  ],
  version: "1.8.9",
  hero: {
    headline:
      "<span class='text-primary'>Video Downloader Pro</span> — Download Videos in HD, 4K & 8K From Any Website",
    subheadline:
      "The ultimate Chrome extension for downloading videos in HD, 4K, 8K, and more. Smart video detection, thumbnail previews, and unlimited downloads with PRO.",
    ctaText: "Install Free",
    badgeText: "v1.8.9 • Last updated Nov 3, 2026",
  },
  features: [
    {
      title: "Universal Compatibility",
      description:
        "Works on 1000+ websites including streaming platforms, video sites, and embedded players. Download from YouTube, Vimeo, and more.",
      icon: "HiDownload",
    },
    {
      title: "Safe & Secure",
      description:
        "Chrome extension that works directly in your browser. No software installation, no malware, no data collection.",
      icon: "HiShieldCheck",
    },
    {
      title: "Multiple Formats",
      description:
        "Download in HD, 4K, 8K, and all available resolutions. Supports MP4, WebM, HLS, DASH, AVI, MOV, MKV, FLV formats.",
      icon: "HiVideoCamera",
    },
  ],
  longFormFeatures: [
    {
      title: "Smart Video Detection",
      description:
        "Proprietary detection engine finds videos that others miss using 4 complementary methods: HTML5 video scanning, embedded player detection, script-based player recognition, and dynamic content monitoring.",
    },
    {
      title: "Thumbnail Previews",
      description:
        "See exactly what you're downloading before clicking download. Generated from video posters, canvas capture, or data attributes for complete confidence.",
    },
    {
      title: "Batch Downloads",
      description:
        "Download all videos from the same page with one click. Perfect for content creators and researchers who need to save entire video collections.",
    },
  ],
  faq: [
    {
      question: "Does Video Downloader Pro work on YouTube?",
      answer:
        "No, Video Downloader Pro does not work on YouTube. This is intentional and respects YouTube's Terms of Service. When you visit YouTube, the extension will show a clear message explaining why it doesn't work there.",
    },
    {
      question: "Is it legal to download videos?",
      answer:
        "Video Downloader Pro is designed for personal, educational, and professional use of publicly available video content. Users are responsible for complying with copyright laws, platform terms of service, and applicable regulations.",
    },
    {
      question: "What video formats does it support?",
      answer:
        "It supports all major video formats and streaming protocols including MP4 (H.264, H.265/HEVC, VP9), WebM (VP8, VP9, AV1), HLS (m3u8 playlists), DASH, AVI, MOV, MKV, FLV, WMV, OGG, 3GP, MPEG, TS, and M2TS.",
    },
    {
      question: "What is the difference between the Free and Pro versions?",
      answer:
        "The Free version allows 5 video downloads per week with access to all quality options (HD, 4K, 8K). The Pro version offers unlimited downloads, batch downloading (all videos with one click), advanced thumbnail generation, and priority download speeds.",
    },
    {
      question: "How does the video detection work?",
      answer:
        "We use proprietary detection technology with 4 complementary methods: HTML5 Video Detection, Embedded Player Detection, Script-Based Player Detection (JW Player, Brightcove, etc.), and Dynamic Content Detection (MutationObserver).",
    },
    {
      question: "Can I download multiple videos at once?",
      answer:
        "Yes! The PRO version supports batch downloads, allowing you to download all videos from the same page with one click. The free version supports single video downloads only.",
    },
    {
      question: "Is my data private and secure?",
      answer:
        "Yes, privacy and security are our top priorities. All video processing happens locally in your browser—we never see or store your downloads. The extension only accesses the websites you visit to detect and download videos.",
    },
    {
      question: "How does Video Downloader Pro compare to Video DownloadHelper?",
      answer:
        'Video Downloader Pro offers broader format support (including HLS, DASH, and 8K), smart detection with thumbnail previews, and a cleaner interface compared to Video DownloadHelper. It also works without requiring a companion app for most downloads. See the <a href="/comparison/video-downloader-pro-vs-video-downloadhelper/" style="color: var(--primary); text-decoration: underline;">full comparison with Video DownloadHelper</a> or browse the <a href="/best/best-video-downloader-chrome-extensions/" style="color: var(--primary); text-decoration: underline;">best video downloader Chrome extensions</a>.',
    },
  ],
  useCases: [
    {
      title: "Content Creators",
      description:
        "Save reference videos, competitor content, and inspiration without leaving your workflow.",
    },
    {
      title: "Students",
      description:
        "Download educational videos, lectures, and training materials for offline study.",
    },
    {
      title: "Marketers",
      description: "Capture competitor videos, testimonials, and marketing content for analysis.",
    },
    {
      title: "Researchers",
      description:
        "Archive video content, documentaries, and historical footage for research projects.",
    },
    {
      title: "Video Editors",
      description:
        "Collect source material, stock footage, and reference videos for editing projects.",
    },
    {
      title: "Podcasters",
      description: "Download video podcasts and interviews for content creation.",
    },
    {
      title: "Journalists",
      description: "Archive breaking news videos, press conferences, and interviews.",
    },
    {
      title: "Business Professionals",
      description: "Download webinars, product demos, and training sessions.",
    },
  ],
  productHunt: {
    slug: "video-downloader-pro-by-plugmonkey",
    id: "video-downloader-pro",
    title: "Video Downloader Pro",
    rating: "4.9",
    reviewCount: "127",
  },
  directoryListings: [
    { directory: "launchstag", url: "https://launchstag.com/p/video-downloader-pro" },
  ],
  demoSection: {
    title: "See Video Downloader Pro in Action",
    description: "Watch how easy it is to download any video.",
    slides: getProductScreenshots("video-downloader-pro"),
  },
  relatedResources: [
    {
      category: "best",
      slug: "best-video-downloader-chrome-extensions",
      title: "Best Video Downloader Chrome Extensions 2026",
      description: "Tested 8 extensions across 1000+ sites. Video Downloader Pro ranks #1.",
    },
    {
      category: "best",
      slug: "best-tiktok-watermark-remover-chrome-extensions",
      title: "Best TikTok Watermark Remover Chrome Extensions",
      description:
        "Stream-capture vs cropping vs blurring — only one approach produces clean output.",
    },
    {
      category: "best",
      slug: "best-twitch-clip-downloader-chrome-extensions",
      title: "Best Twitch Clip + VOD Downloaders",
      description:
        "Clips, sub-only VODs, 8+ hour broadcasts — handled before the 14-day delete clock.",
    },
    {
      category: "download",
      slug: "download-4k-videos",
      title: "How to Download Videos in 4K",
      description: "Top-traffic guide (58 pv/mo): source-quality 4K capture workflow.",
    },
    {
      category: "download",
      slug: "download-youtube-shorts",
      title: "How to Download YouTube Shorts (No Watermark)",
      description: "YouTube Shorts have no native download — extension captures HLS stream.",
    },
    {
      category: "faq",
      slug: "whats-the-best-chrome-extension-for-downloading-videos",
      title: "What's the Best Chrome Video Downloader Extension?",
      description: "Tested-set comparison across 8 extensions — anti-pattern callouts.",
    },
  ],
  testimonials: [
    {
      text: "I archive online courses before they disappear. This caught embedded Vimeo players and HLS streams that two other extensions completely missed. Downloaded 47 lectures in an afternoon.",
      author: "Lena K.",
      role: "Self-taught developer, course hoarder",
      rating: 5,
    },
    {
      text: "Resolution detection is solid — it correctly identified 4K on a site where even youtube-dl grabbed 720p. Only gripe: no batch queue for downloading multiple videos at once. One at a time for now.",
      author: "Carlos V.",
      role: "Freelance video editor",
      rating: 4,
    },
    {
      text: "I teach ESL and save example videos from news sites for class. The thumbnail preview means I never accidentally download an ad. Simple tool, does exactly what it says.",
      author: "Hannah T.",
      role: "ESL teacher in Tokyo",
      rating: 5,
    },
    {
      text: "I cover local politics and archive city council streams before they get pulled offline. It grabbed a 3-hour HLS stream that my screen recorder kept choking on, saved as a clean MP4 on the first try.",
      author: "Priya M.",
      role: "Independent local reporter",
      rating: 5,
    },
    {
      text: "I keep a swipe file of reels and TikToks for clients. The thumbnail preview means I grab the right clip instead of guessing from a filename. Wish it queued a batch, but one at a time is fine for my volume.",
      author: "Theo D.",
      role: "Social media manager, 6 brand accounts",
      rating: 4,
    },
    {
      text: "My daughter's dance studio posts recital videos that vanish after a week. Downloaded all four of hers in 1080p before they disappeared. I am not a tech person and still figured it out in a minute.",
      author: "Ana G.",
      role: "Parent, not a tech person",
      rating: 5,
    },
  ],
  ctaSection: {
    title: "Start Downloading Videos Today",
    subtitle:
      "Join thousands of professionals who trust Video Downloader Pro for their video downloading needs. Try it free, upgrade when you're ready.",
  },
  howTo: {
    name: "How to Download Videos from Any Website",
    description:
      "Learn how to download videos from any website in HD, 4K, 8K using Video Downloader Pro in just 3 simple steps",
    totalTime: "PT30S",
    tool: "Chrome Browser with Video Downloader Pro extension",
    steps: [
      {
        name: "Visit Any Website with Videos",
        text: "Navigate to any website that contains videos you want to download (1000+ video platforms supported).",
        position: 1,
      },
      {
        name: "Click the Smart Detection Button",
        text: "Look for the floating 📺 button in the bottom right corner that appears when videos are detected. Click it to see all available videos with thumbnail previews.",
        position: 2,
      },
      {
        name: "Choose Quality and Download",
        text: "Select your preferred quality (HD, 4K, 8K) and click download. Videos save directly to your Downloads folder.",
        position: 3,
      },
    ],
  },
  homepageCard: {
    backgroundIconName: "HiDownload",
    backgroundIconOpacity: "opacity-10",
    backgroundIconTransform: "transform rotate-6",
    features: [
      { label: "Quality", value: "Up to 8K" },
      { label: "Support", value: "HLS / MP4 / WebM" },
      { label: "Cost", value: "Free / Paid" },
    ],
  },
  displayOrder: 3,
  headerOrder: 600, // Third - shown third in header
  pricing: {
    tagline: "Download Videos from Any Website in HD, 4K, 8K with Smart Detection",
    freeFeatures: [
      "5 video downloads per week",
      "All quality options (HD, 4K, 8K when available)",
      "Basic thumbnail previews",
      "Standard download speeds",
      "Core video detection features",
    ],
    proFeatures: [
      "Unlimited video downloads (no weekly limits)",
      "Advanced thumbnail generation",
      "Batch downloads (all videos with one click)",
      "Priority download speeds",
      "Download history and management",
      "Priority customer support",
      "Early access to new features",
      "All future updates included",
    ],
    useCase:
      "Perfect for content creators, students, marketers, and researchers who need to save online videos",
  },
  supportedPlatforms: {
    title: "Supported Platforms",
    platforms: [
      { name: "X (Twitter)", icon: "FaXTwitter" },
      { name: "Vimeo", icon: "FaVimeoV" },
      { name: "Instagram", icon: "FaInstagram" },
      { name: "Facebook", icon: "FaFacebookF" },
      { name: "LinkedIn", icon: "FaLinkedinIn" },
      { name: "Direct Links", icon: "FaLink" },
    ],
  },
  flowData: {
    installation: {
      headline: "Video Downloader Pro is live.",
      subheadline: "You can now save video from anywhere. Here is how to start.",
      guideSteps: [
        {
          title: "Pin It",
          description:
            "Don't lose it in the menu. Pin Video Downloader Pro to your toolbar for instant access.",
          image: "pin-extension",
        },
        {
          title: "Visit a Page with Video",
          description:
            "Browse to any website with video. A floating 📺 button appears in the bottom-right corner when video is detected.",
          image: "detect-video",
          screenshot: "screenshot-2.png",
          ctaText: "Try on a Video Page",
          ctaUrl: "https://vimeo.com/channels/staffpicks",
        },
        {
          title: "Choose Quality & Download",
          description: "Click the floating button, pick your quality (up to 8K), and hit download.",
          image: "download-video",
          screenshot: "screenshot-1.png",
        },
      ],
      primaryCtaText: "Find a Video and Try It",
      primaryCtaUrl: "https://vimeo.com/channels/staffpicks",
      emailCapture: {
        headline: "Never miss a content trend",
        description:
          "Join our newsletter for content creation tips, video editing hacks, and tool updates.",
        buttonText: "Join the community",
      },
      demoVideo: {
        headline: "Test Your Extension Now",
        description: "Try downloading this stunning 4K video to see Video Downloader Pro in action",
        videoUrl: "https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4",
        videoUrlWebm:
          "https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4",
        posterUrl:
          "https://images.pexels.com/videos/3571264/free-video-3571264.jpg?auto=compress&cs=tinysrgb&w=1200",
        videoDuration: "14s",
        videoResolution: "4K",
        ctaText:
          "Click the floating Video Downloader Pro icon in the bottom right corner and select this video to download in HD, 4K, or any available quality",
        attribution: {
          source: "Pexels",
          authorName: "Tima Miroshnichenko",
          authorUrl: "https://www.pexels.com/@tima-miroshnichenko",
          videoUrl: "https://www.pexels.com/video/colorful-liquid-3571264/",
        },
      },
    },
    uninstallation: {
      headline: "Is it goodbye?",
      subheadline: "We're constantly improving video detection. Tell us what went wrong.",
      survey: {
        question: "What made you uninstall?",
        options: [
          "Couldn't download a specific video",
          "Download speed was too slow",
          "Too many ads/upsells",
          "Found a better tool",
          "Browser performance issues",
        ],
      },
    },
  },
};
